import type {
	GetPublicRegistrationFormResponseDto,
	PortalRegisterRequestDto,
	PortalRegisterResponseDto,
} from '@supply-mover-2-0/shared';
import { getPortalTenantHeader } from '../lib/tenantResolver';

/**
 * Two distinct identifiers are in play and must NOT be conflated:
 *
 *  - The `X-Portal-Tenant` HEADER resolves the tenant DATABASE. The tenant
 *    middleware keys credentials by the ACCOUNT (instance) alias, so this must
 *    be the brand-tenant-qualified value `<brand>-<tenant>` (or the bare tenant
 *    for the default brand) — exactly what `getPortalTenantHeader()` returns.
 *    On a custom domain it returns null and we omit the header, letting the
 *    middleware resolve the tenant from the request Origin instead.
 *  - The `tenantAlias` in the body / query PARAM identifies the PORTAL_SITE
 *    (brand) — the backend looks it up by `portal_site.alias`. That is the
 *    brand alias (e.g. `mci`), which for a multi-brand tenant is NOT the same
 *    as the account alias (`motor_city_ice`).
 *
 * Sending the bare brand alias as the header (the old bug) breaks registration
 * for any storefront whose brand differs from the account — every MCI/HF-style
 * sub-brand — because `<brand>` alone has no tenant credentials.
 */

/** Build request headers with the tenant-resolving header only when present. */
function portalTenantHeaders(base: Record<string, string> = {}): HeadersInit {
	const tenantHeader = getPortalTenantHeader();
	return tenantHeader != null
		? { ...base, 'X-Portal-Tenant': tenantHeader }
		: base;
}

/**
 * Public lead registration — fires unauthenticated. Cannot reuse
 * `portalFetch` because that path injects an Authorization header and
 * runs the 401-refresh dance, neither of which makes sense before the
 * user has an account.
 */
export async function submitRegistration(
	body: PortalRegisterRequestDto,
): Promise<PortalRegisterResponseDto> {
	const url = `${import.meta.env.VITE_API_URL}/api/portal/register`;

	const response = await fetch(url, {
		method: 'POST',
		headers: portalTenantHeaders({ 'Content-Type': 'application/json' }),
		body: JSON.stringify(body),
	});

	// The endpoint returns a structured DTO for both success and
	// failure cases (rate_limited, validation_failed, etc), so we don't
	// throw on !response.ok — the UI surfaces the structured error.
	// Cast target is the DTO; the `'success' in json` check below still
	// guards the empty-object fallback from the .catch at runtime.
	const json = (await response
		.json()
		.catch(() => ({}))) as PortalRegisterResponseDto;

	if ('success' in json) {
		return json;
	}

	// Defensive fallback if the server response was malformed.
	return {
		success: false,
		error: 'unexpected_error',
		message:
			response.status >= 500
				? 'The server is unavailable right now. Please try again shortly.'
				: 'Something went wrong submitting your registration.',
	};
}

/**
 * Fetch the admin-configured supplemental registration questions for a portal
 * (PD-14331). Unauthenticated GET; sends the same X-Portal-Tenant header as the
 * submit call so the tenant middleware can resolve the DB. `tenantAlias` here is
 * the brand alias (portal_site.alias) used to look the brand up; the tenant DB
 * is resolved from the header (see portalTenantHeaders). Returns the resolved
 * field config the form renders dynamically. On any failure it returns a
 * tenant_not_found result so the form simply renders its standard fields.
 */
export async function fetchRegistrationForm(
	tenantAlias: string,
): Promise<GetPublicRegistrationFormResponseDto> {
	const url = `${import.meta.env.VITE_API_URL}/api/portal/registration-form?tenantAlias=${encodeURIComponent(
		tenantAlias,
	)}`;

	try {
		const response = await fetch(url, {
			method: 'GET',
			headers: portalTenantHeaders(),
		});
		// Cast target is the DTO; the `'success' in json` check below still
		// guards the empty-object fallback from the .catch at runtime.
		const json = (await response
			.json()
			.catch(() => ({}))) as GetPublicRegistrationFormResponseDto;
		if ('success' in json) {
			return json;
		}
		return { success: false, error: 'tenant_not_found' };
	} catch {
		return { success: false, error: 'tenant_not_found' };
	}
}
